import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Activity, LogOut, Moon, Pencil, Plus, Sun } from 'lucide-react';
import { isChairRole } from '@dental-flow/rbac';
import { appointmentsApi, type Appointment } from '@/api/appointments.api';
import { chatApi } from '@/api/chat.api';
import { staffApi } from '@/api/clinic.api';
import { notificationsApi } from '@/api/notifications.api';
import { paymentsApi } from '@/api/payments.api';
import { tasksApi } from '@/api/tasks.api';
import { waitlistApi } from '@/api/waitlist.api';
import type { Dashboard } from '@/api/reports.api';
import { useSession } from '@/app/session';
import { centrePersona } from './centre-persona';
import { ROLE_LABELS } from '@/features/settings/StaffDialog';
import { downloadCsv } from '@/lib/export';
import { toIsoDate } from '@/lib/calendar';
import './CommandCentre.css';
import { formatMoney, initialsOf, plural } from '@/lib/format';
import { useTranslation } from 'react-i18next';

interface Props {
  dashboard: Dashboard | undefined;
  /** How many people owe something, and how much — the shell already has both. */
  queuePatients: number;
  queueSum: number;
  tomorrowUnconfirmed: number;
  /** „mâine”, sau „pentru luni” când ziua de lucru care vine nu e mâine (CAL-9). */
  confirmDay?: string;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  onClose: () => void;
}

type Chip = [label: string, value: string | number];

interface CcAction {
  icon: string;
  label: string;
  tone: 'brand' | 'ok' | 'warn' | 'danger' | 'alt' | 'muted';
  onClick: () => void;
  show: boolean;
}

interface CcAlert {
  icon: string;
  label: string;
  sub?: string | null;
  tone: 'ok' | 'warn' | 'danger' | 'info' | 'alt';
  to: string;
  show: boolean;
}

/** A stable empty list, so the derived arrays below do not change identity
 * on every render while the query is still loading. */
const EMPTY: Appointment[] = [];

const p2 = (n: number) => String(n).padStart(2, '0');

/**
 * The command centre, which is a different panel for each role.
 *
 * The original's is not one screen shown to everybody: an accountant sees
 * money split by method, a doctor sees who is in the chair, an assistant sees
 * the material requests waiting on them. Ours showed the same three
 * navigation buttons to all of them — and navigation is what the rail on the
 * left is for. Every button here *does* something: marks an arrival, sends
 * the reminders, exports the day, opens a form already open.
 *
 * The shape is the original's, in its order: the pulse, the quick actions,
 * what needs attention, what is coming, and the clinic's shared task list.
 */
export function CommandCentre({
  dashboard,
  queuePatients,
  queueSum,
  tomorrowUnconfirmed,
  confirmDay = 'mâine',
  theme,
  onToggleTheme,
  onClose,
}: Props) {
  const { t } = useTranslation('common');
  const { user, can, hasFeature, logout } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [note, setNote] = useState<string | null>(null);
  const [newTask, setNewTask] = useState('');

  // Din drepturi, nu din numele rolului: un rol clonat de clinică primește
  // centrul meseriei lui (`centre-persona.ts`).
  const persona = centrePersona(can);
  // The orthodontist gets the doctor's centre: same chair, same day.
  const isMedic = persona === 'medic';
  const isRecept = persona === 'receptie';
  const isAdmin = persona === 'admin';
  const isContab = persona === 'contabil';
  const isAsist = persona === 'asistent';
  const isHr = persona === 'hr';
  /*
   * Ce numește pacienți — lista zilei, sala de așteptare, cererile din portal,
   * fișele — se arată doar cui are drept pe pacienți. HR vede agenda tuturor
   * medicilor (pentru pontaj) și primea cu ea numele pacienților zilei.
   */
  const seesPatients = can('pacienti.vede_toti') || can('pacienti.vede_proprii');
  const title = isMedic
    ? t('centruMedic')
    : isRecept
      ? t('centruReceptie')
      : isAdmin
        ? t('centruAdmin')
        : isContab
          ? t('centruContabil')
          : isAsist
            ? t('centruAsistent')
            : isHr
              ? t('centruHr')
              : t('centruDeComanda');

  const today = toIsoDate(new Date());
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();

  const canCal = can('programari.vede');
  const canMoney = can('incasari.vede');
  // Stocuri switched off: its actions and alerts go with the menu entry.
  const hasStock = hasFeature('inventory');

  const todayAppts = useQuery({
    queryKey: ['shell', 'today', today],
    queryFn: () => appointmentsApi.list({ from: today, to: today }),
    enabled: canCal && seesPatients,
    refetchInterval: 60_000,
  });
  const appts: Appointment[] = todayAppts.data ?? EMPTY;

  /** A doctor's panel counts their own day, not the clinic's. */
  const staffId = user?.staffId ?? null;
  const mine = isMedic && staffId ? appts.filter((a) => a.doctorId === staffId) : appts;

  // Sala de așteptare a medicului e a lui: ortodontul vede și agenda colegilor,
  // dar pacienții lor nu-l așteaptă pe el.
  const waiting = (isMedic ? mine : appts).filter(
    (a) => a.status === 'waiting' || a.status === 'arrived',
  );
  const late = waiting.filter((a) => nowMin - (a.hour * 60 + a.min) > 15);
  const worstWait = waiting.reduce((m, a) => Math.max(m, nowMin - (a.hour * 60 + a.min)), 0);
  const doneCount = mine.filter((a) => a.status === 'done').length;

  /** Cash and card, for the person who closes the day. */
  const totals = useQuery({
    queryKey: ['shell', 'totals', today],
    queryFn: () => paymentsApi.totals(today, today),
    enabled: isContab && canMoney,
    refetchInterval: 60_000,
  });

  const waitlist = useQuery({
    queryKey: ['shell', 'waitlist'],
    queryFn: waitlistApi.summary,
    // The API answers 404 at a clinic without the waiting list.
    enabled: canCal && hasFeature('waitlist'),
    staleTime: 60_000,
  });
  const waitlistCount = waitlist.data?.waiting ?? 0;

  // Necitite peste tot, nu doar pe canale: un mesaj direct e exact cel despre
  // care vrei să afli. Aceeași cheie ca insigna din bară, deci o singură cerere.
  const chatUnread = useQuery({
    queryKey: ['chat', 'unread'],
    queryFn: chatApi.unread,
    enabled: can('chat.foloseste') && hasFeature('teamChat'),
    staleTime: 30_000,
  });
  const unreadMessages = chatUnread.data?.total ?? 0;

  /**
   * Semaforul de medici: cine e în consultație, cine are program, cine e liber.
   *
   * Întrebarea pe care recepția o pune de zece ori pe zi și pe care o
   * răspundea uitându-se pe hol. Numai pentru cine vede toată clinica: unui
   * medic îi arată propria zi, mai sus, nu pe a colegilor.
   */
  const showDoctors = (isAdmin || isRecept) && canCal;
  const roster = useQuery({
    queryKey: ['staff', 'calendar'],
    queryFn: () => staffApi.list({ calendarOnly: true }),
    enabled: showDoctors,
    staleTime: 5 * 60_000,
  });
  const [openDoctor, setOpenDoctor] = useState<string | null>(null);
  const doctors = (roster.data ?? [])
    .filter((d) => isChairRole(d.role))
    .map((d) => {
      const his = appts.filter((a) => a.doctorId === d.id && a.status !== 'cancelled');
      const treating = his.some((a) => a.status === 'treating');
      const next = his
        .filter((a) => a.status !== 'done' && a.hour * 60 + a.min >= nowMin)
        .sort((a, b) => a.hour * 60 + a.min - (b.hour * 60 + b.min))[0];
      return { ...d, count: his.length, treating, next };
    });
  const treatingNow = doctors.filter((d) => d.treating).length;

  /** Cine e în scaunul MEU acum, și cine urmează. Numai pentru medic. */
  const myTreating = isMedic ? mine.find((a) => a.status === 'treating') : undefined;
  const myNext = isMedic
    ? mine
        .filter((a) => a.status !== 'done' && a.hour * 60 + a.min >= nowMin)
        .sort((a, b) => a.hour * 60 + a.min - (b.hour * 60 + b.min))[0]
    : undefined;

  const tasks = useQuery({
    queryKey: ['tasks', 'open'],
    queryFn: () => tasksApi.list(true),
    staleTime: 30_000,
  });
  const openTasks = tasks.data ?? [];

  const addTask = useMutation({
    mutationFn: (text: string) => tasksApi.create({ text }),
    onSuccess: () => {
      setNewTask('');
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
  /** Sarcina corectată pe loc, în loc de ștearsă și scrisă din nou (NOT-19). */
  const [editingTask, setEditingTask] = useState<{ id: string; text: string } | null>(null);
  const renameTask = useMutation({
    mutationFn: (v: { id: string; text: string }) => tasksApi.update(v.id, { text: v.text }),
    onSuccess: () => {
      setEditingTask(null);
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
  const saveTask = () => {
    if (!editingTask) return;
    const text = editingTask.text.trim();
    const was = openTasks.find((t) => t.id === editingTask.id)?.text;
    if (!text || text === was) setEditingTask(null);
    else if (!renameTask.isPending) renameTask.mutate({ id: editingTask.id, text });
  };

  const closeTask = useMutation({
    mutationFn: (id: string) => tasksApi.update(id, { done: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tasks'] }),
  });

  /**
   * Marks the appointment nearest to now as arrived — the reception desk's
   * most frequent single action, and the reason this is a button rather than
   * a link to the calendar.
   */
  const markArrival = useMutation({
    mutationFn: async () => {
      const open = appts.filter(
        (a) => !['done', 'treating', 'noshow', 'arrived', 'cancelled'].includes(a.status),
      );
      if (open.length === 0) throw new Error(t('nicioProgramareDeMarcat'));
      const next = [...open].sort(
        (a, b) => Math.abs(a.hour * 60 + a.min - nowMin) - Math.abs(b.hour * 60 + b.min - nowMin),
      )[0]!;
      await appointmentsApi.setStatus(next.id, 'arrived');
      return next;
    },
    onSuccess: (a) => {
      setNote(t('marcatCaSosit', { name: a.patientName }));
      void qc.invalidateQueries({ queryKey: ['appointments'] });
      void qc.invalidateQueries({ queryKey: ['shell'] });
    },
    onError: (e: Error) => setNote(e.message),
  });

  const sendReminders = useMutation({
    mutationFn: notificationsApi.runReminders,
    onSuccess: (r) =>
      setNote(r.sent > 0 ? t('remindereTrimise', { count: r.sent }) : t('niciunReminderDeTrimis')),
    onError: () => setNote(t('remindereleNuAuPutut')),
  });

  /** The day as a spreadsheet, which is what „Raport zilnic" always was. */
  const dailyReport = () => {
    const rows: unknown[][] = [
      [t('ora'), t('pacient'), t('medic'), t('serviciu'), t('statusCol'), t('pretCol')],
      ...[...appts]
        .sort((a, b) => a.hour * 60 + a.min - (b.hour * 60 + b.min))
        .map((a) => [
          `${p2(a.hour)}:${p2(a.min)}`,
          a.patientName,
          a.doctorName ?? '',
          a.service ?? '',
          a.status,
          a.price ?? 0,
        ]),
    ];
    downloadCsv(`Raport_zilnic_${today}`, rows);
    setNote(t('raportulZileiDescarcat'));
  };

  const go = (to: string) => {
    onClose();
    navigate(to);
  };

  const pulse: Chip[] = isMedic
    ? [
        [t('azi'), mine.length],
        [t('facute'), doneCount],
        [t('inSala'), waiting.length],
      ]
    : isContab
      ? [
          [t('numerar'), formatMoney(totals.data?.cash ?? 0)],
          [t('card'), formatMoney(totals.data?.card ?? 0)],
          [t('deIncasatAzi'), formatMoney(queueSum)],
        ]
      : isAsist
        ? [
            [t('azi'), appts.length],
            [t('inSala'), waiting.length],
            [t('stocCriticPulse'), dashboard?.alerts.lowStock ?? 0],
          ]
        : isHr
          ? [
              [t('inConcediuAzi'), dashboard?.alerts.onLeaveToday ?? 0],
              [t('cereriConcediu'), dashboard?.alerts.pendingLeave ?? 0],
              [t('acteExpira'), dashboard?.alerts.expiringStaffDocs ?? 0],
            ]
          : [
              [t('azi'), appts.length],
              [t('facute'), dashboard?.today.done ?? doneCount],
              // Nimeni în afara celor cu `incasari.vede` — recepția și HR
              // incluse — nu vede cât s-a încasat azi.
              ...(canMoney
                ? ([[t('incasat'), formatMoney(dashboard?.today.revenue ?? 0)]] as Chip[])
                : []),
            ];

  const actions: CcAction[] = [
    {
      icon: '📅',
      label: t('programareNoua'),
      tone: 'brand',
      onClick: () => go('/calendar?new=1'),
      show: can('programari.creeaza') && (isRecept || isAdmin || isMedic),
    },
    {
      icon: '✅',
      label: markArrival.isPending ? t('seMarcheaza') : t('marcheazaSosire'),
      tone: 'ok',
      onClick: () => markArrival.mutate(),
      show: can('programari.modifica') && (isRecept || isAdmin || isAsist),
    },
    {
      icon: '💳',
      label: t('achitareActiune'),
      tone: 'ok',
      onClick: () => go('/payments?record=1'),
      show: can('incasari.inreg') && (isRecept || isAdmin || isContab),
    },
    {
      icon: '👤',
      label: t('pacientNou'),
      tone: 'alt',
      onClick: () => go('/patients?new=1'),
      show: can('pacienti.creeaza') && (isRecept || isAdmin),
    },
    {
      icon: '📤',
      label: sendReminders.isPending
        ? t('seTrimite')
        : tomorrowUnconfirmed
          ? t('trimiteRemindereN', { n: tomorrowUnconfirmed })
          : t('trimiteRemindere'),
      tone: 'alt',
      onClick: () => sendReminders.mutate(),
      // Aceeași cheie pe care o cere serverul; recepția n-o are, deci butonul
      // nu apărea niciodată acolo unde era pus.
      show: can('setari.modifica'),
    },
    {
      icon: '⏳',
      label: waitlistCount ? t('listaAsteptareN', { n: waitlistCount }) : t('listaAsteptare'),
      tone: 'warn',
      onClick: () => go('/waitlist'),
      show: canCal && hasFeature('waitlist') && (isRecept || isAdmin || isMedic),
    },
    {
      icon: '📋',
      label: t('raportZilnic'),
      tone: 'ok',
      onClick: dailyReport,
      // Contabilul n-are drept pe programări: pentru el ziua se închide din
      // Achitări, butonul de mai jos.
      show: canCal && seesPatients && isAdmin,
    },
    {
      icon: '🔐',
      label: t('inchideZiua'),
      tone: 'ok',
      onClick: () => go('/payments'),
      show: can('incasari.inchide_ziua') && (isContab || isAdmin),
    },
    {
      icon: '📉',
      label: t('restante'),
      tone: 'danger',
      onClick: () => go('/payments?tab=debts'),
      show: canMoney && isContab,
    },
    {
      icon: '📊',
      label: t('rapoarteFinanciare'),
      tone: 'alt',
      onClick: () => go('/economy'),
      show: can('rapoarte.financiare') && hasFeature('economy') && isContab,
    },
    {
      icon: '📦',
      label: t('receptieMarfa'),
      tone: 'alt',
      onClick: () => go('/inventory'),
      show: can('stoc.gestioneaza') && hasStock && isAsist,
    },
    {
      icon: '📝',
      // Fără număr: cel de aici era al cererilor din portal, nu al materialelor.
      label: t('cereriMateriale'),
      tone: 'alt',
      onClick: () => go('/inventory'),
      show: can('stoc.vede') && hasStock && isAsist,
    },
    {
      icon: '🌴',
      label: t('concedii'),
      tone: 'brand',
      onClick: () => go('/hr?tab=leave'),
      show: isHr && can('concedii.vede') && hasFeature('hr'),
    },
    {
      icon: '📄',
      label: t('documentePersonal'),
      tone: 'alt',
      onClick: () => go('/hr?tab=documents'),
      show: isHr && can('personal.documente') && hasFeature('hr'),
    },
  ].filter((a) => a.show) as CcAction[];

  const alerts: CcAlert[] = (
    [
      {
        icon: '⏰',
        label: `${t('pacientiAsteapta', { count: late.length })} ${t('peste15Min')}`,
        sub: worstWait > 0 ? t('celMaiMultMin', { n: worstWait }) : null,
        tone: 'danger',
        to: '/calendar',
        show: late.length > 0,
      },
      {
        icon: '🟡',
        label: t('nInSalaAsteptare', { n: waiting.length }),
        sub: t('chiarAcum'),
        tone: 'info',
        to: '/calendar',
        show: waiting.length > 0 && late.length === 0,
      },
      {
        icon: '💰',
        label: t('sumaDeIncasat', { sum: formatMoney(queueSum) }),
        sub: `${t('lucrariNeachitate', { count: queuePatients })}`,
        tone: 'ok',
        to: '/payments',
        // Banii clinicii nu sunt în centrul medicului (hotărârea din 18.09).
        show: canMoney && !isMedic && queuePatients > 0,
      },
      {
        icon: '📞',
        label: t('nDeConfirmat', { n: tomorrowUnconfirmed, day: confirmDay }),
        sub: t('programariNeconfirmate'),
        tone: 'warn',
        // Lista de sunat, nu calendarul întreg: acolo cele neconfirmate trebuiau
        // găsite card cu card.
        to: hasFeature('waitlist') ? '/waitlist?tab=confirmare' : '/calendar',
        show: canCal && tomorrowUnconfirmed > 0 && (isRecept || isAdmin),
      },
      {
        icon: '📉',
        label: t('rateRestante', { count: dashboard?.alerts.overdueInstallments ?? 0 }),
        sub: t('deSunat'),
        tone: 'danger',
        to: '/payments?tab=debts',
        show: canMoney && !isMedic && (dashboard?.alerts.overdueInstallments ?? 0) > 0,
      },
      {
        /*
         * Programările cerute din portal. Câmpul se afișa „cereri de materiale",
         * cu link spre Stocuri și doar cui vede stocul — asistenta vedea
         * cererile pacienților sub alt nume, iar recepția, care le confirmă,
         * nu le vedea deloc.
         */
        icon: '🌐',
        label: `${plural(
          dashboard?.alerts.pendingRequests ?? 0,
          'programare din portal',
          'programări din portal',
        )} de confirmat`,
        sub: t('ceruteDePacientiInca'),
        tone: 'warn',
        // Lista întreagă stă pe Așteptare → Din portal; fără modulul acela,
        // ziua primei cereri din calendar.
        to: hasFeature('waitlist')
          ? '/waitlist?tab=portal'
          : dashboard?.portalRequests[0]
            ? `/calendar?date=${dashboard.portalRequests[0].date}`
            : '/calendar',
        show:
          can('programari.modifica') &&
          can('programari.vede_toti_medicii') &&
          (dashboard?.alerts.pendingRequests ?? 0) > 0,
      },
      {
        icon: '🌴',
        label: plural(
          dashboard?.alerts.pendingLeave ?? 0,
          'cerere de concediu',
          'cereri de concediu',
        ),
        sub: t('deDecis'),
        tone: 'warn',
        to: '/hr?tab=leave',
        show:
          (isAdmin || isHr) &&
          can('concedii.aproba') &&
          hasFeature('hr') &&
          (dashboard?.alerts.pendingLeave ?? 0) > 0,
      },
      {
        icon: '📄',
        label: plural(
          dashboard?.alerts.expiringStaffDocs ?? 0,
          'act de personal expiră',
          'acte de personal expiră',
        ),
        sub: t('inUrmatoarele60De'),
        tone: 'warn',
        to: '/hr?tab=documents',
        show:
          (isAdmin || isHr) &&
          can('personal.vede') &&
          hasFeature('hr') &&
          (dashboard?.alerts.expiringStaffDocs ?? 0) > 0,
      },
      {
        icon: '🦷',
        label: plural(
          (isMedic ? dashboard?.alerts.myOverdueLab : dashboard?.alerts.overdueLab) ?? 0,
          'lucrare de laborator întârziată',
          'lucrări de laborator întârziate',
        ),
        sub: isMedic ? 'comandate de tine' : 'termenul a trecut',
        tone: 'danger',
        to: '/lab',
        show:
          (isMedic || isAdmin) &&
          can('laborator.vede') &&
          hasFeature('dentalLab') &&
          ((isMedic ? dashboard?.alerts.myOverdueLab : dashboard?.alerts.overdueLab) ?? 0) > 0,
      },
      {
        icon: '🔴',
        label: t('nStocCritic', { n: dashboard?.alerts.lowStock ?? 0 }),
        sub: t('subPragulMinim'),
        tone: 'danger',
        to: '/inventory',
        show: can('stoc.vede') && hasStock && (dashboard?.alerts.lowStock ?? 0) > 0,
      },
      {
        icon: '⏳',
        label: t('nLoturiExpira', { n: dashboard?.alerts.expiringLots ?? 0 }),
        sub: t('inUrmatoarele30De'),
        tone: 'warn',
        to: '/inventory',
        show: can('stoc.vede') && hasStock && (dashboard?.alerts.expiringLots ?? 0) > 0,
      },
      {
        icon: '💬',
        label: `${t('mesajeNoi', { count: unreadMessages })}`,
        sub: t('chatEchipa'),
        tone: 'alt',
        to: '/chat',
        show: unreadMessages > 0,
      },
    ] as CcAlert[]
  ).filter((a) => a.show);

  const upcoming = (dashboard?.upcoming ?? []).slice(0, 4);
  // Cererile din portal: pentru cine le confirmă (recepția, adminul).
  const portalRequests =
    (isRecept || isAdmin) && can('programari.modifica') ? (dashboard?.portalRequests ?? []) : [];
  // Fișele rămase în ciornă: ale medicului, din zilele trecute și de azi.
  const draftVisits = isMedic ? (dashboard?.draftVisits ?? []) : [];

  return (
    <aside className="cc" aria-label={t('centruComanda')}>
      <header className="cc__head">
        <Activity size={13} aria-hidden />
        <h2>{title}</h2>
        <button
          type="button"
          className="cc__theme"
          onClick={onToggleTheme}
          aria-label={theme === 'dark' ? t('treciPeTemaLuminoasa') : t('treciPeTemaIntunecata')}
          title={theme === 'dark' ? t('temaLuminoasa') : t('temaIntunecata')}
        >
          {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
        </button>
      </header>

      <div className="cc__pulse">
        {pulse.map(([label, value]) => (
          <div key={label} className="cc__pulsecell">
            <strong className="tabular">{value}</strong>
            <span>{label}</span>
          </div>
        ))}
      </div>

      {/* Pentru medic, sub puls: cine e în scaun acum. E prima întrebare a
          dimineții și singura care nu se poate răspunde din memorie. */}
      {isMedic && canCal ? (
        <button type="button" className="cc__chair" onClick={() => go('/calendar')}>
          {myTreating ? (
            <>
              <span className="cc__chairlabel">{t('inScaunAcum')}</span>
              <strong>{myTreating.patientName}</strong>
              <span className="cc__chairsub">
                {String(myTreating.hour).padStart(2, '0')}:{String(myTreating.min).padStart(2, '0')}
                {myTreating.service ? ` · ${myTreating.service}` : ''}
              </span>
            </>
          ) : myNext ? (
            <>
              <span className="cc__chairlabel">{t('urmatorul')}</span>
              <strong>{myNext.patientName}</strong>
              <span className="cc__chairsub">
                {String(myNext.hour).padStart(2, '0')}:{String(myNext.min).padStart(2, '0')}
                {myNext.service ? ` · ${myNext.service}` : ''}
              </span>
            </>
          ) : (
            <>
              <span className="cc__chairlabel">{t('inScaunAcum')}</span>
              <strong>{t('niciunPacientInContinuare')}</strong>
            </>
          )}
        </button>
      ) : null}

      {actions.length > 0 ? (
        <>
          <h3 className="cc__label">{t('actiuniRapide')}</h3>
          <div className="cc__actions">
            {actions.map((a) => (
              <button
                key={a.label}
                type="button"
                className={`cc__action cc__action--${a.tone}`}
                onClick={a.onClick}
              >
                <span aria-hidden>{a.icon}</span>
                {a.label}
              </button>
            ))}
          </div>
          {note ? (
            <p className="cc__note" role="status">
              {note}
            </p>
          ) : null}
        </>
      ) : null}

      {/* Asistentul virtual are acum pictograma lui animată în bară și un panou
          propriu (AssistantPanel), nu mai stă înghesuit aici. */}

      <h3 className="cc__label">{t('necesitaAtentie')}</h3>
      {alerts.length === 0 ? (
        <p className="cc__quiet">{t('nimicUrgentZiLinistita')}</p>
      ) : (
        <ul className="cc__alerts">
          {alerts.map((a) => (
            <li key={a.label}>
              <button
                type="button"
                className={`cc__alert cc__alert--${a.tone}`}
                onClick={() => go(a.to)}
              >
                <span aria-hidden>{a.icon}</span>
                <span className="cc__alerttext">
                  <strong>{a.label}</strong>
                  {a.sub ? <small>{a.sub}</small> : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {portalRequests.length > 0 ? (
        <>
          <h3 className="cc__label">{t('cereriDinPortal')}</h3>
          <ul className="cc__next">
            {portalRequests.map((r) => (
              <li key={r.id}>
                <button type="button" onClick={() => go(`/calendar?date=${r.date}`)}>
                  <span className="cc__nexttime tabular">
                    {r.date.slice(8, 10)}.{r.date.slice(5, 7)} {r.time}
                  </span>
                  <span className="cc__nextname">{r.patientName}</span>
                  <span className="cc__nextsvc">{r.doctorName ?? ''}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {draftVisits.length > 0 ? (
        <>
          <h3 className="cc__label">
            {t('fiseDeFinalizat', { v: dashboard?.alerts.myDraftVisits ?? draftVisits.length })}
          </h3>
          <ul className="cc__next">
            {draftVisits.map((v) => (
              <li key={v.id}>
                <button
                  type="button"
                  onClick={() => go(`/patients/${v.patientId}/visits?visit=${v.id}`)}
                >
                  <span className="cc__nexttime tabular">
                    {v.date.slice(8, 10)}.{v.date.slice(5, 7)}
                  </span>
                  <span className="cc__nextname">{v.patientName}</span>
                  <span className="cc__nextsvc">{t('ciorna2')}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {canCal && seesPatients ? (
        <>
          <h3 className="cc__label">{t('urmatoareleProgramari')}</h3>
          {upcoming.length === 0 ? (
            <p className="cc__quiet">{t('nicioProgramare')}</p>
          ) : (
            <ul className="cc__next">
              {upcoming.map((u) => (
                <li key={u.id}>
                  <button type="button" onClick={() => go('/calendar')}>
                    <span className="cc__nexttime tabular">{u.time}</span>
                    <span className="cc__nextname">{u.patientName}</span>
                    <span className="cc__nextsvc">{u.service ?? ''}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}

      {showDoctors && doctors.length > 0 ? (
        <>
          <h3 className="cc__label">
            {t('mediciAzi')} · {t('activi', { count: doctors.filter((d) => d.count > 0).length })}
            {treatingNow > 0 ? ` · ${t('nInConsultatie', { n: treatingNow })}` : ''}
          </h3>
          <div className="cc__docs">
            {doctors.map((d) => {
              const state = d.treating
                ? t('inConsultatieAcum')
                : d.count > 0
                  ? `${t('programariAzi', { count: d.count })}${
                      d.next
                        ? ` · următorul ${String(d.next.hour).padStart(2, '0')}:${String(
                            d.next.min,
                          ).padStart(2, '0')}`
                        : t('aTerminat')
                    }`
                  : t('liberAzi');
              return (
                <button
                  key={d.id}
                  type="button"
                  className={`cc__doc${openDoctor === d.id ? ' cc__doc--on' : ''}`}
                  title={`${d.name} — ${state}`}
                  aria-label={`${d.name} — ${state}`}
                  onClick={() => setOpenDoctor((v) => (v === d.id ? null : d.id))}
                >
                  <span
                    className="cc__docpill"
                    style={{ background: d.count > 0 ? d.color : undefined }}
                  >
                    {d.initials ?? d.name.slice(0, 2).toUpperCase()}
                  </span>
                  <span
                    className={`cc__docdot cc__docdot--${
                      d.treating ? 'busy' : d.count > 0 ? 'ok' : 'free'
                    }`}
                    aria-hidden
                  />
                </button>
              );
            })}
          </div>
          {openDoctor ? (
            <p className="cc__docdetail">
              <strong>{doctors.find((d) => d.id === openDoctor)?.name}</strong>
              <span>
                {(() => {
                  const d = doctors.find((x) => x.id === openDoctor);
                  if (!d) return null;
                  if (d.treating) return t('inConsultatieAcum');
                  if (d.count === 0) return t('liberAzi');
                  return `${t('programariAzi2', { count: d.count })}${
                    d.next
                      ? ` · următorul ${String(d.next.hour).padStart(2, '0')}:${String(
                          d.next.min,
                        ).padStart(2, '0')}`
                      : t('aTerminat')
                  }`;
                })()}
              </span>
            </p>
          ) : null}
        </>
      ) : null}

      <h3 className="cc__label">
        {t('taskUriHeader')}
        {openTasks.length > 0 ? ` · ${t('nDeFacut', { n: openTasks.length })}` : ''}
      </h3>
      <form
        className="cc__taskadd"
        onSubmit={(e) => {
          e.preventDefault();
          const text = newTask.trim();
          if (text) addTask.mutate(text);
        }}
      >
        <input
          value={newTask}
          onChange={(e) => setNewTask(e.target.value)}
          placeholder={t('taskNou')}
          aria-label={t('taskNou2')}
        />
        <button
          type="submit"
          disabled={!newTask.trim() || addTask.isPending}
          aria-label={t('adauga')}
        >
          <Plus size={14} />
        </button>
      </form>
      <ul className="cc__tasks">
        {openTasks.slice(0, 8).map((task) =>
          editingTask?.id === task.id ? (
            <li key={task.id}>
              <form
                className="cc__taskadd cc__taskedit"
                onSubmit={(e) => {
                  e.preventDefault();
                  saveTask();
                }}
              >
                <input
                  autoFocus
                  value={editingTask.text}
                  maxLength={300}
                  onChange={(e) => setEditingTask({ id: task.id, text: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setEditingTask(null);
                  }}
                  onBlur={saveTask}
                  aria-label={t('corecteazaText', { text: task.text })}
                />
              </form>
            </li>
          ) : (
            <li key={task.id} className="cc__task">
              <label>
                <input
                  type="checkbox"
                  checked={false}
                  onChange={() => closeTask.mutate(task.id)}
                  aria-label={t('marcheazaFacut', { text: task.text })}
                />
                <span>{task.text}</span>
              </label>
              <button
                type="button"
                className="cc__taskpen"
                onClick={() => setEditingTask({ id: task.id, text: task.text })}
                aria-label={t('corecteazaText', { text: task.text })}
                title={t('corecteaza')}
              >
                <Pencil size={13} />
              </button>
            </li>
          ),
        )}
      </ul>

      {/* Cine e conectat, și ieșirea din cont.
          Stătea jos în bara de navigare, unde motivul scris era „contul e al
          aplicației, nu al paginii". Motivul e bun și duce aici: panoul ăsta
          *e* nivelul aplicației — ține deja tema — iar bara din stânga e
          pentru navigare. În plus, în bara îngustă numele se rupea la mijlocul
          cuvântului („Cont demonstrat/iv"); aici are lățimea panoului. */}
      <div className="cc__account">
        <div className="cc__acctavatar" aria-hidden>
          {initialsOf(user?.name)}
        </div>
        <div className="cc__acctinfo">
          <span className="cc__acctname" title={user?.name}>
            {user?.name}
          </span>
          <span className="cc__acctrole">
            {(user?.role && ROLE_LABELS[user.role]) ?? user?.role}
          </span>
        </div>
        <button
          type="button"
          className="cc__acctout"
          onClick={() => void logout()}
          aria-label={t('iesiDinCont')}
          title={t('iesiDinCont')}
        >
          <LogOut size={16} />
        </button>
      </div>
    </aside>
  );
}
